import {
  Component,
  inject,
  signal,
  computed,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ElementRef,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  TransferNotificationService,
  TransferNotification,
} from '../../../core/services/transfer-notification.service';
import { StockTransferService } from '../../../core/services/stock-transfer.service';
import { AuthService } from '../../../core/services/auth.service';
import { BusinessContextService } from '../../../core/services/multi-tenant/business-context.service';
import { FancySnackbarService } from '../../services/fancy-snackbar.service';
import { Subject, takeUntil, combineLatest, filter } from 'rxjs';

@Component({
  selector: 'app-transfer-notifications',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule, MatBadgeModule, MatButtonModule, MatTooltipModule],
  template: `
    <div class="transfer-notifications">
      <!-- Bell Button -->
      <button
        class="transfer-notifications__bell"
        [class.transfer-notifications__bell--active]="isOpen()"
        [matBadge]="unreadCount() > 0 ? unreadCount() : null"
        [matBadgeHidden]="unreadCount() === 0"
        matBadgeColor="warn"
        matBadgeSize="small"
        matTooltip="Transferencias pendientes"
        (click)="toggleDropdown($event)"
      >
        <mat-icon>notifications</mat-icon>
      </button>

      <!-- Dropdown -->
      @if (isOpen()) {
        <div class="transfer-notifications__dropdown">
          <!-- Header -->
          <div class="transfer-notifications__header">
            <mat-icon>notifications</mat-icon>
            <span class="transfer-notifications__title">Notificaciones</span>
            @if (unreadCount() > 0) {
              <span class="transfer-notifications__count">({{ unreadCount() }})</span>
            }
            @if (unreadCount() > 0) {
              <button class="transfer-notifications__mark-read" (click)="markAllAsRead($event)">
                Marcar todo como leído
              </button>
            }
          </div>

          <!-- Notifications List -->
          <div class="transfer-notifications__list">
            @if (notifications().length === 0) {
              <div class="transfer-notifications__empty">
                <mat-icon>inbox</mat-icon>
                <span>No hay notificaciones</span>
              </div>
            } @else {
              @for (notification of notifications(); track notification.id) {
                <div
                  class="transfer-notifications__item"
                  [class.transfer-notifications__item--unread]="!notification.read"
                  (click)="onNotificationClick(notification, $event)"
                >
                  <div class="transfer-notifications__item-icon">
                    <mat-icon>{{ getIcon(notification.type) }}</mat-icon>
                  </div>
                  <div class="transfer-notifications__item-content">
                    <span class="transfer-notifications__item-title">
                      {{ getTitle(notification.type) }}
                    </span>
                    <span class="transfer-notifications__item-product">
                      {{ notification.transfer.productName }}
                      @if (notification.transfer.variantName) {
                        - {{ notification.transfer.variantName }}
                      }
                    </span>
                    <span class="transfer-notifications__item-quantity">
                      {{ notification.transfer.quantity }} unidades
                    </span>
                    <span class="transfer-notifications__item-from">
                      desde {{ notification.transfer.fromLocationName }}
                    </span>
                    <span class="transfer-notifications__item-time">
                      {{ getTimeAgo(notification.createdAt) }}
                    </span>
                  </div>
                  <div class="transfer-notifications__item-actions">
                    @if (notification.transfer.status === 'in-transit') {
                      <button
                        class="transfer-notifications__item-action transfer-notifications__item-action--primary"
                        [disabled]="processingTransferId() === notification.transfer.id"
                        (click)="quickAcceptTransfer(notification, $event)"
                      >
                        @if (processingTransferId() === notification.transfer.id) {
                          <mat-icon class="transfer-notifications__spinner">sync</mat-icon>
                        } @else {
                          Recibir
                        }
                      </button>
                    }
                    <button
                      class="transfer-notifications__item-action"
                      (click)="viewTransfer(notification, $event)"
                    >
                      Ver
                    </button>
                  </div>
                </div>
              }
            }
          </div>

          <!-- Footer -->
          <div class="transfer-notifications__footer">
            <button class="transfer-notifications__view-all" (click)="viewAllTransfers($event)">
              Ver todas las transferencias
            </button>
          </div>
        </div>
      }
    </div>
  `,
  styleUrl: './transfer-notifications.component.scss',
})
export class TransferNotificationsComponent implements OnInit, OnDestroy {
  private notificationService = inject(TransferNotificationService);
  private transferService = inject(StockTransferService);
  private authService = inject(AuthService);
  private businessContextService = inject(BusinessContextService);
  private snackBar = inject(FancySnackbarService);
  private router = inject(Router);
  private elementRef = inject(ElementRef);
  private destroy$ = new Subject<void>();

  isOpen = signal(false);
  processingTransferId = signal<string | null>(null);
  notifications = this.notificationService.notifications;
  unreadCount = this.notificationService.unreadCount;

  ngOnInit(): void {
    // Subscribe to business and location context changes to update notification subscription
    combineLatest([
      this.businessContextService.currentBusinessId$,
      this.businessContextService.currentLocationId$,
    ])
      .pipe(
        takeUntil(this.destroy$),
        filter(([businessId, locationId]) => !!businessId && !!locationId)
      )
      .subscribe(([businessId, locationId]) => {
        this.notificationService.subscribeToLocationTransfers(businessId!, locationId!);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.notificationService.unsubscribe();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen.set(false);
    }
  }

  toggleDropdown(event: Event): void {
    event.stopPropagation();
    this.isOpen.update(v => !v);
  }

  markAllAsRead(event: Event): void {
    event.stopPropagation();
    this.notificationService.markAllAsRead();
  }

  onNotificationClick(notification: TransferNotification, event: Event): void {
    event.stopPropagation();
    this.notificationService.markAsRead(notification.id);
  }

  viewTransfer(notification: TransferNotification, event: Event): void {
    event.stopPropagation();
    this.notificationService.markAsRead(notification.id);
    this.isOpen.set(false);
    this.router.navigate(['/inventory/transfers']);
  }

  async quickAcceptTransfer(notification: TransferNotification, event: Event): Promise<void> {
    event.stopPropagation();

    const transfer = notification.transfer;
    const businessId = this.authService.currentBusinessId();
    const currentUser = this.authService.currentUser();

    if (!businessId || !currentUser || !transfer.id) {
      this.snackBar.error('Error al procesar la transferencia', 'Cerrar');
      return;
    }

    this.processingTransferId.set(transfer.id);

    try {
      // Quick accept with full quantity
      await this.transferService.receiveTransfer(
        transfer.id,
        transfer.quantity, // Accept full quantity
        currentUser.uid,
        currentUser.displayName || currentUser.email || 'Usuario'
      );

      this.notificationService.markAsRead(notification.id);
      this.snackBar.success(
        `Transferencia recibida: ${transfer.quantity} ${transfer.productName}`,
        'Cerrar'
      );
    } catch (error) {
      console.error('Error receiving transfer:', error);
      this.snackBar.error('Error al recibir la transferencia', 'Cerrar');
    } finally {
      this.processingTransferId.set(null);
    }
  }

  viewAllTransfers(event: Event): void {
    event.stopPropagation();
    this.isOpen.set(false);
    this.router.navigate(['/inventory/transfers']);
  }

  getIcon(type: string): string {
    return this.notificationService.getNotificationIcon(type as any);
  }

  getTitle(type: string): string {
    return this.notificationService.getNotificationTitle(type as any);
  }

  getTimeAgo(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - new Date(date).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'ahora';
    if (diffMins < 60) return `hace ${diffMins} min`;
    if (diffHours < 24) return `hace ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
    return `hace ${diffDays} día${diffDays > 1 ? 's' : ''}`;
  }
}
